/**
 * <nosto-pdp-view> refreshes Nosto's PDP recommendations when a combined
 * listing swatch swaps the product in place (no page reload).
 *
 * custom.js fires `combined-listing:section-replaced` once the new product's
 * section is in the DOM and the URL has been updated. Nosto's page-load tagging
 * still describes the original product, so we send a product view for the new
 * one and re-render the page's placements.
 */
if (!customElements.get('nosto-pdp-view')) {
  const DATA_ID_PREFIX = 'CombinedListingData-';

  // Module-level so it survives if the element itself is ever re-rendered.
  let lastProductId = null;

  const readProductId = (sectionId) => {
    const dataEl = document.getElementById(`${DATA_ID_PREFIX}${sectionId}`);
    if (!dataEl) return null;

    try {
      const { productId } = JSON.parse(dataEl.textContent);
      return productId ? String(productId) : null;
    } catch {
      return null;
    }
  };

  const hasRequiredApi = (api) =>
    typeof api?.createRecommendationRequest === 'function' &&
    typeof api.placements?.getPlacements === 'function' &&
    typeof api.placements?.injectCampaigns === 'function';

  // Nosto's own script also reacts to the swap and resends a product view from
  // the page-load tagging markup, which still holds the original product's id.
  // That request lands after ours and re-renders the original recommendations,
  // so point the markup at the new product.
  const syncTaggingMarkup = (productId) => {
    const taggedId = document.querySelector('.nosto_product .product_id');
    if (taggedId) taggedId.textContent = productId;
  };

  class NostoPdpView extends HTMLElement {
    connectedCallback() {
      window.nostojs ||= (callback) => {
        (window.nostojs.q ||= []).push(callback);
      };

      // Seed with the product the page loaded with; Nosto's own tagging covers it.
      if (lastProductId === null) {
        const initialData = document.querySelector(`[id^="${DATA_ID_PREFIX}"]`);
        lastProductId = initialData
          ? readProductId(initialData.id.slice(DATA_ID_PREFIX.length))
          : null;
      }

      document.addEventListener('combined-listing:section-replaced', this.onSectionReplaced);
    }

    disconnectedCallback() {
      document.removeEventListener('combined-listing:section-replaced', this.onSectionReplaced);
    }

    onSectionReplaced = (event) => {
      const productId = readProductId(event.detail?.sectionId);

      // Skips the current product, which also de-dupes back-to-back swaps.
      if (!productId || productId === lastProductId) return;
      lastProductId = productId;

      syncTaggingMarkup(productId);
      window.nostojs((api) => this.refreshRecommendations(api, productId));
    };

    async refreshRecommendations(api, productId) {
      if (!hasRequiredApi(api)) {
        console.warn('[Nosto PDP] API missing expected methods; skipping product view');
        return;
      }

      try {
        const product = { product_id: productId };
        const skuId = new URLSearchParams(window.location.search).get('variant');
        if (skuId) product.selected_sku_id = skuId;

        // Variant view event, per Nosto's "Sending Product-View Events" docs.
        const request = api
          .createRecommendationRequest({ includeTagging: true })
          .setProducts([product])
          .setElements(api.placements.getPlacements());

        // load() defaults to JSON, which injectCampaigns can't render.
        request.setResponseMode('HTML');
        const { recommendations } = await request.load();

        // The shopper may have switched colors again while this was in flight.
        if (productId !== lastProductId) return;
        api.placements.injectCampaigns(recommendations);
      } catch (error) {
        console.warn('[Nosto PDP] product view failed', error);
      }
    }
  }

  customElements.define('nosto-pdp-view', NostoPdpView);
}
